import { useAdaptiveRtStore } from '@/stores/adaptiveRtStore';
import { restoreAdaptiveRtFromHistoryPayload } from '@/composables/useAdaptiveRtHistoryRestore';
import { readCalculationSession, persistCourseWorkspaceSession } from '@/utils/calculationSessionStorage';
import { loadCourseWorkspaceRestore } from '@/utils/courseWorkspaceRestore';
import { tryRestoreAdaptiveRtDraft } from '@/utils/adaptiveRtDraftPersist';
import { isAdaptiveRtModule, normalizeModuleId } from '@/utils/adaptiveRtModule';
import { normalizeCourseId, courseIdsEqual } from '@/utils/courseId';

function sessionMatchesCourse(session, courseId) {
  if (!session?.mode || !isAdaptiveRtModule(session.mode)) return false;
  const sessionCourseId = normalizeCourseId(session?.openIntent?.courseId);
  return courseIdsEqual(sessionCourseId, courseId);
}

async function restoreFromHistoryPayload(payload) {
  if (!payload?.runRecords?.length || !isAdaptiveRtModule(payload.mode)) {
    return false;
  }
  return restoreAdaptiveRtFromHistoryPayload(payload);
}

/**
 * Восстановить этапы AdaptiveRT: локальный черновик → сессия курса → последний прогон истории.
 * @param {number|string|null} courseId
 * @param {{ skipIfStagesHavePlans?: boolean }} [opts]
 * @returns {Promise<{ source: 'draft'|'session'|'history', restored: boolean, message?: string }|null>}
 */
export async function tryRestoreAdaptiveRtCourseWorkspace(courseId, opts = {}) {
  const cid = normalizeCourseId(courseId);
  if (cid == null) return null;

  const store = useAdaptiveRtStore();
  const draftResult = await tryRestoreAdaptiveRtDraft(cid, opts);
  if (draftResult?.restored) {
    return {
      source: 'draft',
      restored: true,
      message: draftResult.message,
      gatePassed: draftResult.gatePassed,
    };
  }
  if (opts.skipIfStagesHavePlans && store.planIds.length > 0) {
    return { source: 'draft', restored: true };
  }

  const session = readCalculationSession();
  if (sessionMatchesCourse(session, cid)) {
    const restored = await restoreFromHistoryPayload(session);
    if (restored) {
      return { source: 'session', restored: true };
    }
  }

  const modeId = normalizeModuleId('adaptive_rt');
  const workspace = await loadCourseWorkspaceRestore({ courseId: cid, modeId });
  if (workspace) {
    const restored = await restoreFromHistoryPayload(workspace);
    if (restored) {
      persistCourseWorkspaceSession(workspace);
      return { source: 'history', restored: true };
    }
  }

  return null;
}
